import { useEffect, useState } from 'react'
import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import Spinner from '@/components/Spinner/Spinner'
import { useTheme } from '@/components/ThemeProvider/ThemeProvider'
import { PieChartType } from '@/types/page'
import { useBreakpoints } from '@/utils/useBreakpoints'

import styles from './TypeDistributionPanel.module.scss'

type Props = {
  chartData: PieChartType
}

const TypeDistributionPanel = ({ chartData }: Props) => {
  const { content, title } = chartData
  const [isLoaded, setIsLoaded] = useState(false)
  const [theme] = useTheme()
  const { isSm } = useBreakpoints()

  // recharts does not render properly on the server, so we delay it until the client
  useEffect(() => {
    setIsLoaded(true)
  }, [])

  return (
    <div className={`${styles.typeDistribution} d-flex flex-column flex-grow-1 p-2`}>
      <h5 className='d-flex justify-content-center w-100'>{title || 'Average Type Distribution'}</h5>
      <div className=' align-items-center d-flex h-100 justify-content-center'>
        {!isLoaded ? (
          <Spinner className='fa-2x' />
        ) : content ? (
          <ResponsiveContainer aspect={2} width='100%'>
            <PieChart>
              <Pie
                data={content.map(({ color, label, value }) => ({
                  fill: color,
                  label,
                  value,
                }))}
                dataKey='value'
                endAngle={90}
                isAnimationActive={false}
                label={({ cx, cy, index, innerRadius, midAngle, outerRadius, value }) => {
                  const RADIAN = Math.PI / 180
                  const radius = (isSm ? 25 : 8) + innerRadius + (outerRadius - innerRadius)
                  const x = cx + radius * Math.cos(-midAngle * RADIAN)
                  const y = cy + radius * Math.sin(-midAngle * RADIAN)
                  return (
                    <text
                      dominantBaseline='central'
                      fill={content[index].color}
                      fontSize={isSm ? 12 : 10}
                      textAnchor={x > cx ? 'start' : 'end'}
                      x={x}
                      y={y}
                    >
                      {content[index].label} ({value})
                    </text>
                  )
                }}
                labelLine={isSm}
                nameKey='label'
                outerRadius={'75%'}
                startAngle={450}
              />
              <Tooltip contentStyle={{ background: theme === 'dark' ? '#000' : '#fff' }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className={styles.noData}>No data</div>
        )}
      </div>
    </div>
  )
}

export default TypeDistributionPanel
